import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, FormInstance, InputGroup, Modal, Message, Button, ModalProps, NumberInput} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel} from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import { getCitiesLookup, getCity } from '@/services/city.service';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import { City } from '@/models/city.model';
import { Icon } from '@rsuite/icons';
import { MdContentCopy, MdLink } from 'react-icons/md';
import { ApproveScrapedRunEvent, ApproveScrapedDistance, ScrapedEvent } from '@/models/scraped-event.model';
import MapToSelectPath from '@/components/MapToSelectPath';
import { utc_iana_time_zone_id } from '@/constants/time';
import { approveScrapedEvent } from '@/services/scraped-event.service';
import EventLinksEditor from '@/components/EventLinksEditor';
import { cleanEventLinks } from '@/models/event-link.model';
import ScrapedDistancesEditor, { ScrapedDistanceRow, defaultRouteIndex, rowsFromScraped, rowsToRequest } from './scraped-distances-editor';
import Textarea from '@/components/Textarea';

const runEventSchema = Schema.Model<ApproveScrapedRunEvent>({
  name: Schema.Types.StringType().isRequired('This field is required.'),
  description: Schema.Types.StringType().isRequired('This field is required.'),
  website: Schema.Types.StringType(),
  imageUrl: Schema.Types.StringType(),
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  registrationDate: Schema.Types.DateType(),
  path: Schema.Types.ArrayType(),
  distance: Schema.Types.NumberType().when(schemaSpec => {
    const path = schemaSpec.path.value as Array<any> | undefined;
    if (path && path?.length > 0) {
        return Schema.Types.NumberType().isRequired('This field is required when route information is available.');
    }
    return Schema.Types.NumberType();
  }),
  startDate: Schema.Types.DateType().when(schemaSpec => {
    const path = schemaSpec.path.value as Array<any> | undefined;
    if (path && path?.length > 0) {
        return Schema.Types.DateType().isRequired('This field is required when route information is available.');
    }
    return Schema.Types.DateType();
  }),
});


const emptyModel: ApproveScrapedRunEvent = {
  name: undefined,
  description: undefined,
  cityId: undefined,
  imageUrl: undefined,
  website: undefined,
  registrationDate: undefined,
  startDate: undefined,
  distance: undefined,
  path: undefined,
};

interface ApproveScrapedRunEventModalProps extends ModalProps{
  formObject?: ScrapedEvent;
};

const ApproveScrapedRunEventModal: React.FC<ApproveScrapedRunEventModalProps> = ({formObject, open, onClose}) => {
  const [formValue, setFormValue] = useState<ApproveScrapedRunEvent>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState< City | undefined>(undefined);
  const [hasDistance, setHasDistance] = useState<boolean>(false);
  // Scraped races (multi-distance events). When present they replace the single distance/route fields.
  const [raceRows, setRaceRows] = useState<ScrapedDistanceRow[]>([]);
  const [routeIndex, setRouteIndex] = useState<number | undefined>(undefined);

  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
    
    useEffect(() => {
        if(formObject){
            const rows = rowsFromScraped(formObject.distances, false);
            setRaceRows(rows);
            setRouteIndex(defaultRouteIndex(rows, formObject.path));
            const anyPath = rows.length === 0 && formObject.path && formObject.path.length > 0;
            setFormValue(pre => ({...pre, 
                name: formObject?.name,
                description: formObject?.description,
                imageUrl: formObject?.imageUrl,
                registrationDate: formObject.registrationDate,
                cityId: formObject.cityId,
                website: formObject.sourceWebsiteUrl,
                links: formObject.links ?? [],
                path: anyPath ? formObject.path : undefined,
                startDate: anyPath ? formObject.startDate : undefined
            }));
            setHasDistance(anyPath);
            if(formObject.cityId){
                updateSelectedCity(formObject.cityId);
            }
        }else {
            setFormValue(emptyModel);
            setHasDistance(false);
        }

    }, [formObject]);
    
    const handleSubmit = async () => {
    if(!formObject || !formObject.eventType) return;

    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    let distances: ApproveScrapedDistance[] | undefined = undefined;
    if(raceRows.length > 0){
      const res = rowsToRequest(raceRows, routeIndex, formObject.path);
      if(typeof res === 'string'){
        setErrorModel({ success: false, errorMessage: res, errorCode: 0 });
        return;
      }
      distances = res;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await approveScrapedEvent({
        scrapedEventId: formObject?.id,
        eventType: formObject.eventType,
        runEvent: {...formValue, distances, links: cleanEventLinks(formValue.links)}
      });
      if (result.success) {
        nav('/run-events/' + result.result);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const updateSelectedCity = async(cityId: number|undefined) => {
    if(!cityId){
      setSelectedCity(undefined);
      return;
    }

    const city = (await getCity(cityId)).result;
    setSelectedCity(city);
    setFormValue(pre => ({...pre, countryId: city?.country?.id}));
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };
  
  const handleCityChange = (value) => {
    setFormValue(pre => ({ ...pre, cityId: value, runEventKeyId: undefined}));
    updateSelectedCity(value);
  };

  const handleModalClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setSelectedCity(undefined);
    setRaceRows([]);
    setRouteIndex(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  return (
    <Modal open={open} onClose={handleModalClose} size="lg" backdrop={'static'}>
        <Modal.Header>
            <Modal.Title>Approve Scraped Run Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form name='approve-run-event-form' fluid 
                ref={formRef}
                onChange={handleFormChange}
                onCheck={setFormError}
                formValue={formValue}
                model={runEventSchema}
                disabled={loading}
            >
            <Grid w='100%' fluid>
                <Row>
                    <Col span={{xs:24}} >
                        <Form.Label>Image</Form.Label>              
                        <Form.Control
                            accepter={ImageUploader}
                            name="imageUrl"
                            height="300px"
                            url={formValue.imageUrl}
                            objectStorageCategory={ObjectStorageFileCategory.RunEvent}
                            clearable={false}
                            uploadable={false}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24}} >
                    <Form.Group>
                        <Form.Label>Name </Form.Label>
                        <Form.Control name="name" value={formValue.name ?? ''}/>
                    </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24}} >
                    <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control name="description" accepter={Textarea} rows={3}/>
                    </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24, xl:12}} >
                        <Form.Group controlId='cityId'>
                            <Form.Label>City</Form.Label>
                            <Form.Control name="cityId" accepter={FetchingSelect} block
                            fetchData={(text, page) => getCitiesLookup(text, undefined, page)} 
                            placeholder="Select a city"
                            onChange={handleCityChange}
                            reFetchOnOpen = {true}
                            /> 
                        </Form.Group>
                    </Col>
                    <Col span={{xs:24, xl:12}}>
                        <Form.Group>
                            <Form.Label>Registration Date</Form.Label>
                            <Form.Control accepter={ZonedDatePicker} block
                            name="registrationDate" 
                            value={formValue.registrationDate ?? null}
                            onChange={dt => setFormValue(pre => ({...pre, registrationDate: dt ?? undefined}))}
                            zoneIanaId={selectedCity?.ianaTimeZoneId ?? utc_iana_time_zone_id}
                            />
                        </Form.Group>                          
                    </Col>                    
                </Row>
                <Row>
                    <Col span={{xs:24}}>
                        <Form.Group>
                            <Form.Label>Website</Form.Label>
                            <InputGroup inside>
                            <InputGroup.Button onClick={() => formValue?.website && window.open(formValue?.website, '_blank') }>
                                <Icon as={MdLink} />
                            </InputGroup.Button>
                            <Form.Control name="website" />
                            <InputGroup.Button onClick={() => navigator.clipboard.writeText(formValue?.website || '')}>
                                <Icon as={MdContentCopy} />
                            </InputGroup.Button>
                            </InputGroup>                     
                        </Form.Group>                 
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24}}>
                        <Form.Group>
                            <Form.Label>Links</Form.Label>
                            <EventLinksEditor value={formValue.links} disabled={loading}
                                onChange={links => setFormValue(pre => ({...pre, links}))} />
                        </Form.Group>
                    </Col>
                </Row>
                { raceRows.length > 0 && <>
                    <ScrapedDistancesEditor
                        rows={raceRows}
                        onChange={setRaceRows}
                        routeIndex={routeIndex}
                        onRouteIndexChange={setRouteIndex}
                        hasRoute={!!formObject?.path && formObject.path.length > 0}
                        showType={false}
                        zoneIanaId={selectedCity?.ianaTimeZoneId}
                        disabled={loading}
                    />
                    {formObject?.path && formObject.path.length > 0 && <Row>
                        <Col span={{xs:24}}>
                        <MapToSelectPath
                            value={formObject?.path}
                            height={`400px`}
                            zoom={13}
                            disabled
                            options= {{scrollwheel: false}}
                        />
                        </Col>
                    </Row>}
                </>}
                { raceRows.length === 0 && !hasDistance && <Message showIcon type="warning">
                    The Scraped Run Event does not have route information. You can approve it as a Run Event Event without distance details, but users will not be able to track runs for this event until distance and route are added.
                </Message>}
                { raceRows.length === 0 && hasDistance && <>
                    <Row>
                    <Col span={{xs:24, xl:12}}>
                        <Form.Group>
                            <Form.Label>Distance</Form.Label>
                            <Form.Control name="distance" accepter={NumberInput}/> 
                        </Form.Group>                  
                    </Col>
                    <Col span={{xs:24, xl:12}}>
                        <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                            <Form.Control accepter={ZonedDatePicker} block
                            name="startDate" 
                            value={formValue.startDate ?? null}
                            onChange={dt => setFormValue(pre => ({...pre, startDate: dt ?? undefined}))}
                            zoneIanaId={selectedCity?.ianaTimeZoneId ?? utc_iana_time_zone_id}
                            />                  
                        </Form.Group>
                    </Col>
                    </Row>
                    <Row>
                        <Col span={{xs:24}}>
                        <MapToSelectPath
                            value={formObject?.path}
                            height={`400px`}
                            zoom={13}
                            disabled
                            options= {{scrollwheel: false}}
                        />
                        </Col>                        
                    </Row>
                </>}
            </Grid>
            {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
        </Form>
        </Modal.Body>
        <Modal.Footer>
            <Stack justifyContent='space-between' style={{paddingTop: 15}}>
                <Button onClick={handleModalClose} appearance="subtle" disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} appearance="primary" loading={loading}>
                    Approve
                </Button>
            </Stack>
        </Modal.Footer>       
    </Modal>
  );
};

export default ApproveScrapedRunEventModal;

